import { useEffect, useState } from "react";
import { Box, Button, Flex, useToast } from "@chakra-ui/react";
import { Modal } from "../../Modal";
import { ButtonCustom } from "../../../form/controls/form.button";
import { FormInput } from "../../../form/controls/form.input";
import { successToast } from "../../../../constants/toast.constants";
import { integrationService } from "../../../../service/integrations/integration.service";
import { DropdownInput } from "../../../form/controls/dropdown.input";
import { IntegrationTarget } from "../../../../models/enum/integration/integration-target.enum";
import { useDispatch } from "react-redux";
import { addIntegration } from "../../../../store/slice/Account/user-profile.slice";

export const AddIntegrationModal = ({ isOpen, onClose, onSubmit }) => {
  const toast = useToast();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [testFailed, setTestFailed] = useState(false);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  const [formState, setFormState] = useState({
    name: null,
    apiKey: null,
    apiUrl: null,
    target: IntegrationTarget.KANDJI,
  });

  const onInputChange = (event: any) => {
    let { name, value } = event.target;

    setFormState({
      ...formState,
      [name]: value,
    });
  };

  const handleSubmit = async (e: any) => {
    e?.preventDefault();
    setLoading(true);
    setTestFailed(false);
    setDisclaimerAccepted(true);

    try {
      await integrationService
        .addIntegration({
          ...formState,
          target: formState.target.toUpperCase(),
        })
        .then((resp) => {
          dispatch(
            addIntegration({
              target: formState.target.toUpperCase(),
              status: "ACTIVE",
            })
          );
          toast(successToast(`Integration has been successfully added!`));
          setLoading(false);
          setTestFailed(false);
          onSubmit();
        });
    } catch (error) {
      setTestFailed(true);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormState({
      name: null,
      apiKey: null,
      apiUrl: null,
      target: IntegrationTarget.KANDJI,
    });

    setTestFailed(false);
    setLoading(false);
    onClose();
  };

  useEffect(() => {
    return () => {};
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      header="Add Integration"
      width="600px"
      description={
        <Flex flexDir="column" gap="8px">
          <Box
            border="1px solid"
            color="orange.500"
            borderColor="orange.500"
            borderRadius="8px"
            p="12px"
            bgColor="orange.100"
          >
            Currently, only Apple devices are supported. We're actively working on
            adding support for Android and Windows devices — stay tuned for updates!
          </Box>
          <Box>
            Add a new integration to your account and sync data between other
            systems.
          </Box>
          <Box>
            For set-up instructions,{" "}
            <a
              href="https://www.zolo.com.au/articles/how-to-setup-kandji-integration-with-zolo-single-touch"
              target="_blank"
              rel="noreferrer"
              style={{
                fontStyle: "italic",
                textDecoration: "underline",
                fontWeight: "semibold",
                color: "#265542",
              }}
            >
              click here
            </a>
            .
          </Box>
        </Flex>
      }
      onClose={loading ? null : handleClose}
    >
      {testFailed && (
        <Box
          color="red.500"
          bgColor="red.100"
          fontSize="14px"
          mb="12px"
          p="12px"
          borderRadius="8px"
        >
          Failed to verify the API Integration details. Please check the Token
          and URL and try again.
        </Box>
      )}
      <form onSubmit={handleSubmit}>
        <Flex flexDir="column" gap="20px">
          <FormInput
            required
            name="apiUrl"
            title="API URL"
            placeholder="yourcompany.api.kandji.io"
            onChange={onInputChange}
          />
          <FormInput
            required
            name="apiKey"
            title="API Token"
            placeholder="API Token"
            onChange={onInputChange}
            autoComplete="off"
          />
          <DropdownInput
            required
            styles={{ width: "100%" }}
            name="target"
            title="Target"
            defaultValue={"KANDJI"}
            options={IntegrationTarget}
            onChange={onInputChange}
          />

          <Flex mt="12px" gap="12px" justifyContent="flex-end">
            <ButtonCustom
              secondary
              neutral
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </ButtonCustom>
            <ButtonCustom
              loading={loading}
              disabled={loading}
              onClick={(e: any) => {
                if (!disclaimerAccepted) {
                  setDisclaimerOpen(true);
                } else {
                  handleSubmit(e);
                }
              }}
            >
              Add Integration
            </ButtonCustom>
          </Flex>
        </Flex>
      </form>

      <DisclaimerModal
        isOpen={disclaimerOpen}
        onClose={() => setDisclaimerOpen(false)}
        handleSubmit={handleSubmit}
      />
    </Modal>
  );
};

const DisclaimerModal = ({ isOpen, onClose, handleSubmit }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} header="Disclaimer">
      <Flex flexDir="column" gap="20px">
        <Box>
          <Box mb="12px">
            Before you connect, please review the following information about
            this integration:
          </Box>

          <Box mb="12px">
            <Box fontWeight="semibold" mb="8px">
              This integration uses Kandji API with permissions:
            </Box>
            <Box as="ul" pl="20px" mb="12px">
              <Box as="li" mb="4px">
                [Read] Device List
              </Box>
              <Box as="li" mb="4px">
                [Read] Device ID
              </Box>
              <Box as="li" mb="4px">
                [Read] Device Details
              </Box>
              <Box as="li" mb="4px">
                [Read] List Tags
              </Box>
              <Box as="li" mb="4px">
                [Write] Create Tag
              </Box>
            </Box>
          </Box>

          <Box>
            This means Zolo can see device information, but cannot edit,
            un-enroll, or delete anything in your Kandji account.
          </Box>
        </Box>

        <Flex flexDir="row" gap="8px" justifyContent="space-between">
          <Flex flexDir="column" alignItems="center" gap="4px">
            <img
              src="/assets/certifications/iso-9001.png"
              alt="ISO 9001"
              width="70px"
              height="70px"
            />
            <Box fontSize="12px" fontWeight="semibold">
              ISO 9001
            </Box>
          </Flex>
          <Flex flexDir="column" alignItems="center" gap="4px">
            <img
              src="/assets/certifications/iso-14001.png"
              alt="ISO 14001"
              width="70px"
              height="70px"
            />
            <Box fontSize="12px" fontWeight="semibold">
              ISO 14001
            </Box>
          </Flex>
          <Flex flexDir="column" alignItems="center" gap="4px">
            <img
              src="/assets/certifications/iso-27001.png"
              alt="ISO 27001"
              width="70px"
              height="70px"
            />
            <Box fontSize="12px" fontWeight="semibold">
              ISO 27001
            </Box>
          </Flex>
          <Flex flexDir="column" alignItems="center" gap="4px">
            <img
              src="/assets/certifications/iso-45001.png"
              alt="ISO 45001"
              width="70px"
              height="70px"
            />
            <Box fontSize="12px" fontWeight="semibold">
              ISO 45001
            </Box>
          </Flex>
          <Flex flexDir="column" alignItems="center" gap="4px">
            <img
              src="/assets/certifications/iso-as-5377.png"
              alt="ISO AS 5377"
              width="70px"
              height="70px"
            />
            <Box fontSize="12px" fontWeight="semibold">
              AS NZS 5377
            </Box>
          </Flex>
        </Flex>

        <Flex mt="16px" gap="8px" justifyContent="flex-end">
          <ButtonCustom secondary neutral onClick={onClose}>
            Cancel
          </ButtonCustom>
          <ButtonCustom
            onClick={() => {
              handleSubmit();
              onClose();
            }}
          >
            I Accept
          </ButtonCustom>
        </Flex>
      </Flex>
    </Modal>
  );
};
